// Category folding for DonutChart. A donut/pie is an ALL-PAIRS chart form
// (dataviz skill, color-formula.md check 4): any two slices sit side by side
// on the ring, so every pair needs CVD separation, and the validated
// --rc-chart-1..8 palette only clears that bar on its first 3 slots (see the
// comment on those tokens in tokens.css, and chart-color.ts's
// CHART_CATEGORICAL_SLOTS doc). A BI-defined category list is open-ended and
// arbitrary at compile time, so DonutChart cannot trust a caller to remember
// the cap — it folds internally, unconditionally, every render.
import type { DonutSlice } from "./chart-types";

export interface FoldedDonutSlice extends DonutSlice {
  /** True for the single synthetic "everything past the top 3" slice. Never
   *  true for a real category, even one that happens to be labeled "Other"
   *  upstream — this flag is what routes color assignment to the shared
   *  neutral fold-in slot instead of a categorical one. */
  isOther: boolean;
}

/** The key `foldPieSlicesToThree` gives its synthetic Other slice. Exported so
 *  a caller building its own legend/tooltip copy can recognize it without
 *  string-matching a label a locale might translate. */
export const DONUT_OTHER_KEY = "__donut_other__";

/**
 * Ranks `slices` by value descending, keeps the top 3, and folds everything
 * else into one synthetic "Other" slice (sum of the rest) appended last.
 *
 * Zero and negative values are dropped before ranking: a zero-share category
 * draws no arc and would only crowd the legend, and a negative value has no
 * meaningful angular share on a part-of-whole chart. Ties break on input
 * order (stable sort), so a caller feeding already-sorted data keeps it.
 *
 * ≤3 non-zero slices: returned as-is, sorted by value descending, no Other
 * appended (there is nothing left to fold). This is also what makes the
 * function idempotent — folding an already-folded result is a no-op.
 */
export function foldPieSlicesToThree(slices: readonly DonutSlice[]): FoldedDonutSlice[] {
  const positive = slices.filter((s) => Number.isFinite(s.value) && s.value > 0);
  const ranked = [...positive].sort((a, b) => b.value - a.value);
  const top = ranked.slice(0, 3).map((s) => ({ ...s, isOther: false }));
  const rest = ranked.slice(3);
  if (rest.length === 0) return top;
  const otherTotal = rest.reduce((sum, s) => sum + s.value, 0);
  return [...top, { key: DONUT_OTHER_KEY, label: "Other", value: otherTotal, isOther: true }];
}
